import { convertObjToQueryStr } from "@utils/utils"
import type { AnyObject } from "yup"
import { api } from "./api"
import type { JsonResponse, WithId, components, paths } from "./generated"

export type Video = WithId<components["schemas"]["Video"]>
export type VideoPublicizationStatus = components["schemas"]["VideoPublicizationStatus"]
export type PublicVideoPlayback = components["schemas"]["PublicVideoPlayback"]
// GET /video/{id}?withPermissions=true augments Video with the caller's permission strings (documented on the endpoint response)
type VideoWithPermissions = WithId<JsonResponse<paths["/api/video/{id}"]["get"]>>

type Paginated<T> = Omit<components["schemas"]["PaginationObj"], "docs"> & { docs: T[] }

type RootGroupVideosRes = { docs?: Video[]; general?: Video[]; rootGroup?: Video[] } | Video[]

const videoApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getVideoPublicization: builder.query<VideoPublicizationStatus, string>({
			query: (id) => ({ url: `/video/${id}/publicize` }),
			providesTags: (_result, _error, id) => [{ type: "Video", id: `public-${id}` }],
		}),
		publicizeVideo: builder.mutation<VideoPublicizationStatus, string>({
			query: (id) => ({ url: `/video/${id}/publicize`, method: "POST", body: {} }),
			invalidatesTags: (_result, _error, id) => [{ type: "Video", id: `public-${id}` }],
		}),
		revokeVideoPublicization: builder.mutation<void, string>({
			query: (id) => ({ url: `/video/${id}/publicize`, method: "DELETE" }),
			invalidatesTags: (_result, _error, id) => [{ type: "Video", id: `public-${id}` }],
		}),
		getVideo: builder.query<Video, string>({
			query: (id) => ({
				url: `/video/${id}`,
			}),
			providesTags: (_result, _error, args) => {
				return [{ type: "Video", id: args }]
			},
		}),
		getVideoWithPermissions: builder.query<VideoWithPermissions, string>({
			query: (id) => ({
				url: `/video/${id}?withPermissions=true`,
			}),
			providesTags: (_result, _error, args) => {
				return [{ type: "Video", id: args }]
			},
		}),
		search: builder.query<Paginated<Video>, { query?: AnyObject }>({
			query: ({ query }) => ({
				url: `/video/search/${convertObjToQueryStr(query)}`,
			}),
			providesTags: () => {
				return ["Video"]
			},
		}),
		updateVideo: builder.mutation<Video, { id: string; body: AnyObject }>({
			query: ({ id, body }) => ({
				url: `/video/${id}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return ["Video", { type: "Video", id: args.id }]
			},
		}),
		updateMultipleVideos: builder.mutation<Video[], { videoIds: string[]; data?: AnyObject }>({
			query: (body) => ({
				url: "/video/updateMultiple",
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				const videoInvalidation = args.videoIds.map((id) => ({ type: "Video" as const, id }))
				return ["Video" as const, ...videoInvalidation]
			},
		}),
		deleteVideo: builder.mutation<void, string>({
			query: (id) => ({
				url: `/video/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, args) => {
				return ["Video", { type: "Video", id: args }]
			},
		}),
		uploadGeneral: builder.mutation<Video, { body: AnyObject }>({
			query: ({ body }) => ({
				url: "/video/upload/general",
				method: "POST",
				body,
			}),
			invalidatesTags: (result) => {
				return ["Video", { type: "Video", id: result?._id }]
			},
		}),
		uploadRootGroup: builder.mutation<Video, { id: string; body: AnyObject }>({
			query: ({ id, body }) => ({
				url: `/video/upload/rootGroup/${id}`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return ["Video", { type: "Video", id: args.id }]
			},
		}),
		linkToEvent: builder.mutation<void, { videoId: string; rootGroupId: string; body?: AnyObject }>({
			query: ({ videoId, rootGroupId, body }) => ({
				url: `/video/${videoId}/link/${rootGroupId}`,
				method: "POST",
				body: body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [
					"Video" as const,
					{ type: "Video" as const, id: args.videoId },
					...(args.body?.linkedEvents?.map((eId: string) => ({ type: "Event" as const, id: eId })) ?? []),
				]
			},
		}),
		listGeneralVideos: builder.query<Paginated<Video> | Video[], { query: AnyObject }>({
			query: ({ query }) => ({
				url: `video/general${convertObjToQueryStr(query)}`,
			}),
			providesTags: (result, _error, args) => {
				if (args.query.paginate) {
					return [
						"Video" as const,
						...((result as Paginated<Video>)?.docs?.map((v) => ({ type: "Video" as const, id: v._id })) ?? []),
					]
				} else {
					return ["Video" as const, ...((result as Video[])?.map((v) => ({ type: "Video" as const, id: v._id })) ?? [])]
				}
			},
		}),
		listRootGroupVideos: builder.query<RootGroupVideosRes, { id: string; query?: AnyObject }>({
			query: ({ id, query }) => ({
				url: `video/rootGroup/${id}${convertObjToQueryStr(query)}`,
			}),
			providesTags: (result) => {
				const res = result as RootGroupVideosRes | undefined
				const sourceVideos = res && !Array.isArray(res) && res.docs ? res.docs : res
				let videoList: Video[] = []
				const sv = sourceVideos as { general?: Video[]; rootGroup?: Video[] } | undefined
				if (sv?.general && sv?.rootGroup) {
					const generalVids = sv.general ?? []
					const rootGroupVids = sv.rootGroup ?? []

					videoList = [...generalVids, ...rootGroupVids]
				}

				return ["Video" as const, ...videoList.map((v) => ({ type: "Video" as const, id: v?._id }))]
			},
		}),
		getVideoPlayerUrl: builder.query<JsonResponse<paths["/api/video/{id}/playerUrl"]["get"]>, string>({
			query: (id) => ({
				url: `video/${id}/playerUrl`,
			}),
		}),
		createIcareusAccount: builder.mutation<
			JsonResponse<paths["/api/video/rootGroupAccount/{id}/createIcareusAccount"]["post"]>,
			{ rootGroupId: string; body?: AnyObject }
		>({
			query: ({ rootGroupId, body }) => ({
				url: `/video/rootGroupAccount/${rootGroupId}/createIcareusAccount`,
				method: "POST",
				body: body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return ["Video", "RootGroup", { type: "RootGroup", id: args.rootGroupId }]
			},
		}),
		deleteIcareusAccount: builder.mutation<
			JsonResponse<paths["/api/video/rootGroupAccount/{id}/deleteIcareusAccount"]["post"]>,
			{ rootGroupId: string }
		>({
			query: ({ rootGroupId }) => ({
				url: `/video/rootGroupAccount/${rootGroupId}/deleteIcareusAccount`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, args) => {
				return ["Video", "RootGroup", { type: "RootGroup", id: args.rootGroupId }]
			},
		}),
		restoreIcareusAccount: builder.mutation<
			JsonResponse<paths["/api/video/rootGroupAccount/{id}/restoreIcareusAccount"]["post"]>,
			{ rootGroupId: string; organizationId?: number }
		>({
			query: ({ rootGroupId, organizationId }) => ({
				url: `/video/rootGroupAccount/${rootGroupId}/restoreIcareusAccount`,
				method: "POST",
				body: { organizationId },
			}),
			invalidatesTags: (_result, _error, args) => {
				return ["Video", "RootGroup", { type: "RootGroup", id: args.rootGroupId }]
			},
		}),
		getTrimmerToken: builder.query<JsonResponse<paths["/api/video/{id}/trimmer/token"]["get"]>, string>({
			query: (id) => ({
				url: `/video/${id}/trimmer/token`,
			}),
		}),
		getEligibleEventLinkableVideos: builder.query<Paginated<Video>, AnyObject>({
			query: (queryObj) => ({
				url: `/video/getEligibleEventLinkable${convertObjToQueryStr(queryObj)}`,
			}),
			providesTags: (result, error) => {
				if (error || !result) return []
				return [...result.docs.map((doc) => ({ type: "Video" as const, id: doc._id })), { type: "Video" as const, id: "LIST" }]
			},
		}),

		// Uses multipart/form-data
		uploadMultipleVideos: builder.mutation<Video[], { formData: FormData; query?: AnyObject; rootGroupId?: string }>({
			query: ({ formData, query }) => ({
				url: `/video/uploadMultiple${convertObjToQueryStr(query)}`,
				body: formData,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, args) => {
				return ["Video", "RootGroup", { type: "RootGroup", id: args.rootGroupId }]
			},
		}),

		getUsedCapacityGeneral: builder.query<JsonResponse<paths["/api/video/capacity"]["get"]>, void>({
			query: () => ({
				url: "/video/capacity",
			}),
			providesTags: () => ["Video"],
		}),

		getUsedCapacityRootGroup: builder.query<JsonResponse<paths["/api/video/capacity/{id}"]["get"]>, { id: string }>({
			query: ({ id }) => ({
				url: `/video/capacity/${id}`,
			}),
			providesTags: () => ["Video"],
		}),

		syncIcareusVideos: builder.mutation<{ insertedCount?: number }, { rootGroup?: string }>({
			query: ({ rootGroup }) => ({
				url: `/video/sync${rootGroup ? `?rootGroup=${rootGroup}` : ""}`,
				method: "POST",
			}),
			invalidatesTags: (result) => {
				if (result?.insertedCount === 0) {
					return []
				}
				return ["Video"]
			},
		}),
	}),
})

export const {
	useGetVideoPublicizationQuery,
	usePublicizeVideoMutation,
	useRevokeVideoPublicizationMutation,
	useGetVideoQuery,
	useGetVideoWithPermissionsQuery,
	useUpdateVideoMutation,
	useUpdateMultipleVideosMutation,
	useDeleteVideoMutation,
	useLinkToEventMutation,
	useListGeneralVideosQuery,
	useListRootGroupVideosQuery,
	useCreateIcareusAccountMutation,
	useDeleteIcareusAccountMutation,
	useRestoreIcareusAccountMutation,
	useGetTrimmerTokenQuery,
	useLazyGetEligibleEventLinkableVideosQuery,
	useGetVideoPlayerUrlQuery,
	useGetUsedCapacityGeneralQuery,
	useGetUsedCapacityRootGroupQuery,
	useSyncIcareusVideosMutation,
} = videoApi
